import type { MouseEvent } from 'react'
import type { TvWithPrice } from '~/shared/interfaces'
import type { ViewRow } from '../../model/components/index.h'

import { PrimaryBaseInlineFavoriteToggleButton } from "/app/libraries/arcana/src/atoms/ActionButton/Templates/index.ts"
import { TopTvSimpleCard, TVSimpleCard } from "/app/libraries/arcana/src/blocks/Cards/TVCard/index.ts"

import { useUnit } from 'effector-react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { model as router } from '~/core/router'
import {
  model as favoritesFeatureModel,
  model as favoritesModel,
} from '~/features/favorites'
import { isPaid } from '~/features/payment'
import * as pinLockFeature from '~/features/pin-lock'
import { model as guestModel } from '~/features/subscriber/guest'
import { resolveNewLabel } from '~/shared/tools'
import { TvCardHorizontalCoversMap } from '~/shared/ui/components/cardCovers'
import { paymentTvModelWidget } from '~/widgets/Payment/model'
import { getCardFonts, getCategoryId, getCollectionId } from '../lib/style'

export const TvChannelCard = ({
  row,
  item,
  idx,
}: {
  row: ViewRow
  item: TvWithPrice
  idx: number
}) => {
  const {
    logoUrl,
    free,
    rented,
    locked,
    formattedName,
    name,
    id,
    labelPrice,
    priceSettings,
    purchaseInfo,
    channelNumber,
    externalId,
    blockedByAcl,
    labels,
  } = item

  const {
    appearance: {
      resource: { search, path },
      style: { posterSize, topContent },
      title,
    },
  } = row

  const isLocked = useUnit(pinLockFeature.$isLockedFn)
  const isFeatured = path.includes('featured')
  const isFavorite = search.has('only-favorites')
  const isRecentlyWatched = path.includes('recently-watched')

  const { t } = useTranslation()

  // Channels use the short "New" label (top-right), distinct from
  // Movies/Series' full text label (bottom-right). Pin-locked posters
  // already replace their whole content via BaseCard's own `locked` handling.
  const newLabelShort = resolveNewLabel({ labels }, t)

  const { guest, favorites, favorite, selectActiveChannelEvent, routerHref } =
    useUnit({
      guest: guestModel.$guest,
      favorites: favoritesFeatureModel.$favorites,
      favorite: favoritesModel.favorite,
      selectActiveChannelEvent: paymentTvModelWidget.selectActiveChannel,
      routerHref: router.$href,
    })

  const handleUpdateChannel = useCallback(
    (e: MouseEvent<Element>) => {
      e.stopPropagation()
      e.preventDefault()
      favorite({
        type: 'channel',
        id: id!,
        favorite: favorites.channel[id!],
        name: name,
      })
    },
    [favorite, id, favorites, name]
  )

  const categoryId = getCategoryId(search)
  const collectionId = getCollectionId(search)

  const handleTvCardClick = useCallback(
    (e: MouseEvent<Element>) => {
      e.stopPropagation()
      e.preventDefault()
      selectActiveChannelEvent({
        id: id,
        name: name,
        logoUrl: logoUrl ?? '',
        priceSettings: priceSettings,
        purchaseInfo: purchaseInfo ?? null,
        free: Boolean(free),
        channelNumber: channelNumber ?? 0,
        categoryId: categoryId,
        categoryName: title,
        locked: locked,
        redirect: routerHref ?? '',
        externalId: externalId,
        // TODO: Extra fields. Need to review types for unused fields
        condition: false,
        invalid: false,
        disabled: false,
        errorMessage: '',
        onComplete: () => {},
        onChange: () => {},
        valueAfterInvalid: '',
        featured: isFeatured,
        favorite: isFavorite,
        recentlyWatched: isRecentlyWatched,
        collectionId,
      })
    },
    [
      id,
      name,
      logoUrl,
      priceSettings,
      purchaseInfo,
      free,
      channelNumber,
      locked,
      externalId,
      routerHref,
      categoryId,
      collectionId,
      selectActiveChannelEvent,
      isFeatured,
      isFavorite,
      isRecentlyWatched,
      title,
    ]
  )

  const { titleFont } = getCardFonts(posterSize)

  const Card = topContent ? TopTvSimpleCard : TVSimpleCard
  const topNumber = idx + 1

  return (
    <Card
      onClick={handleTvCardClick}
      posterUrl={logoUrl}
      paid={isPaid(item)}
      rented={rented}
      locked={isLocked({ id, locked })}
      aclBlockedBy={Boolean(blockedByAcl)}
      purchaseTitle={labelPrice}
      viewedButtonFavorite={!guest}
      title={formattedName}
      titleFont={titleFont}
      channelNumber={channelNumber}
      covers={TvCardHorizontalCoversMap}
      topNumber={topNumber}
      newLabelShort={newLabelShort}
    >
      <PrimaryBaseInlineFavoriteToggleButton
        selected={favorites.channel[id!]}
        onClick={handleUpdateChannel}
        dataTestId='favorite-btn'
      />
    </Card>
  )
}
